import clsx from 'clsx';
import { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { cn } from '@/utils/classnames';

import { isStepVisible } from './step-config';

import type { JobPostingStep } from '../types';

interface StepperProps {
  handleStepClick: (step: JobPostingStep) => Promise<void>;
}

export function Stepper({ handleStepClick }: StepperProps) {
  const intl = useIntl();
  const { currentStep, selectedChannelSlugs, completedSteps } =
    useJobPostingContext();

  const steps: { value: JobPostingStep; label: string }[] = useMemo(() => {
    return [
      {
        value: 'select-openings',
        label: intl.formatMessage({
          defaultMessage: 'Openings',
          description: 'Job posting stepper: pick openings to post',
        }),
      },
      {
        value: 'select-channels',
        label: intl.formatMessage({
          defaultMessage: 'Channels',
          description: 'Job posting stepper: pick job boards',
        }),
      },
      {
        value: 'job-details',
        label: intl.formatMessage({
          defaultMessage: 'Review Job',
          description: 'Job posting stepper: review opening descriptions',
        }),
      },
      {
        value: 'pool-setup',
        label: intl.formatMessage({
          defaultMessage: 'Pool Setup',
          description: 'Job posting stepper: configure a Pool campaign',
        }),
      },
      {
        value: 'campaign-details',
        label: intl.formatMessage({
          defaultMessage: 'Spend',
          description: 'Job posting stepper: set channel budgets',
        }),
      },
      {
        value: 'summary',
        label: intl.formatMessage({
          defaultMessage: 'Post',
          description: 'Job posting stepper: review and submit',
        }),
      },
    ];
  }, [intl]);

  const visibleSteps = useMemo(
    () => steps.filter(step => isStepVisible(step.value, selectedChannelSlugs)),
    [steps, selectedChannelSlugs],
  );

  return (
    <Breadcrumb>
      <BreadcrumbList className="flex-nowrap">
        {visibleSteps.map((step, visibleIndex) => (
          <Fragment key={step.value}>
            <BreadcrumbItem className="gap-2">
              <span
                className={cn(
                  'bg-fx-neutral-100 text-small text-fx-neutral-400 flex h-4 w-5 items-center justify-center rounded-full px-1 py-0',
                  currentStep === step.value
                    ? 'bg-fx-primary text-white'
                    : completedSteps.has(step.value) &&
                        'bg-fx-green text-white',
                )}
              >
                {visibleIndex + 1}
              </span>
              <Button
                variant="ghost"
                type="button"
                onClick={() => void handleStepClick(step.value)}
                className={clsx(
                  'text-body text-fx-neutral-400 h-auto p-0 font-normal whitespace-nowrap hover:bg-transparent',
                  currentStep === step.value && 'text-fx-neutral-900',
                )}
              >
                {step.label}
              </Button>
            </BreadcrumbItem>
            {visibleIndex < visibleSteps.length - 1 && <BreadcrumbSeparator />}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
